import React, { useState, useMemo, useContext } from 'react';
import { createPortal } from 'react-dom';
import { Form } from 'react-bootstrap';
import ButtonBasic from '../../atoms/buttonBasic/view';
import ButtonPrimary from '../../atoms/ButtonPrimary/view';
import ButtonSecondary from '../../atoms/ButtonSecondary/view';
import { DropzoneArea } from 'mui-file-dropzone';
import { Eye, Trash2, Loader2, AlertCircle, Paperclip } from 'lucide-react';
import { PDFDocument } from 'pdf-lib';

import { firstValueFrom } from 'rxjs';
import { roleContext } from '../../../context/role/context';
import { dispatchCustomEvent } from '../../../utils/dispatch';
import { formatMoney } from '../../../utils/formating';
import { pipelineService } from '../../../services/pipelineService';
import { parseUblInvoice } from '../../../services/xmlParser';

import ModalAtom from '../../atoms/modal/view';
import Table from '../../molecules/tablas/view';
import ButtonAction from '../../atoms/buttonAction/view';
import ConfirmPopUp from '../../atoms/confirmPopUp/view';
import ArrayRelater from '../../molecules/ArrayRelater/view';
import Chip from '../../atoms/chip/container';
import ClientInfo from '../../atoms/ClientInfo/view';
import ServiceLineInput from '../../molecules/ServiceLineInput/view';
import PaymentMethodSelector from '../../molecules/PaymentMethodSelector/view';

import XmlPreview from './molecules/XmlPreview/view';
import BankStatementReconciliation from './molecules/BankStatementReconciliation/view';
import LiveVoucherPreview from './molecules/LiveVoucherPreview/view';
import usePipelineLookups from './hooks/usePipelineLookups';
import usePipelineDrafts from './hooks/usePipelineDrafts';
import usePipelineReview from './hooks/usePipelineReview';
import useBankStatement from './hooks/useBankStatement';
import BuyFromXml from '../buyfromxml/view';
import SpendFromXml from '../SpendFromXml/view';
import './style.css';

function PipelineDraftsManager({ profile, companyId }) {
  const { suscripcion } = useContext(roleContext);
  const isAiQuotaExceeded = useMemo(() => {
    if (!suscripcion?.ai_tokens) return false;
    const { limit, usage, is_unlimited } = suscripcion.ai_tokens;
    return !is_unlimited && usage >= limit;
  }, [suscripcion]);

  // --- Hooks ---
  const lookups = usePipelineLookups({ companyId });
  const draftsHook = usePipelineDrafts({ companyId });
  const review = usePipelineReview({ companyId, profile, lookups, onApproved: draftsHook.fetchDrafts });
  const { bankSubaccounts, liveVoucher } = useBankStatement(review.activeDraft, review.reviewPayload, lookups.subaccounts);

  // --- File Upload Local State ---
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [filePasswords, setFilePasswords] = useState({});
  const [uploading, setUploading] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordTargetFile, setPasswordTargetFile] = useState(null);
  const [pdfPassword, setPdfPassword] = useState('');

  const openPasswordModal = (file) => {
    setPasswordTargetFile(file);
    setPdfPassword('');
    setShowPasswordModal(true);
    setTimeout(() => dispatchCustomEvent('modalpdf-password-modal'), 50);
  };

  const handleUploadSubmit = async (customFiles = selectedFiles, customPasswords = filePasswords) => {
    if (customFiles.length === 0) return;
    setUploading(true);
    try {
      const result = await pipelineService.uploadDocuments(customFiles, companyId, customPasswords);
      const errors = result.data?.errors || [];
      const processed = result.data?.processed_drafts || [];
      const passwordError = errors.find(err => err.error === 'PDF_PASSWORD_REQUIRED' || err.error === 'PDF_PASSWORD_INCORRECT');
      if (passwordError) {
        const target = customFiles.find(f => f.name === passwordError.filename);
        if (target) {
          setUploading(false);
          openPasswordModal(target);
          if (passwordError.error === 'PDF_PASSWORD_INCORRECT') {
            dispatchCustomEvent('popup', { message: `Contraseña incorrecta para '${passwordError.filename}'.`, type: 'error' });
          }
          return;
        }
      }
      if (processed.length > 0) {
        dispatchCustomEvent('popup', { message: `Procesados: ${processed.length} documentos con éxito.`, type: 'success' });
      }
      const otherErrors = errors.filter(err => err.error !== 'PDF_PASSWORD_REQUIRED' && err.error !== 'PDF_PASSWORD_INCORRECT');
      if (otherErrors.length > 0) {
        dispatchCustomEvent('popup', { message: `Archivos con error: ${otherErrors.map(e => e.filename).join(', ')}`, type: 'warning' });
      }
      setSelectedFiles([]);
      setFilePasswords({});
      draftsHook.fetchDrafts(1);
    } catch (e) {
      console.error("[Upload]", e);
      dispatchCustomEvent('popup', { message: e.response?.data?.message || 'Error al subir archivos.', type: 'error' });
    } finally { setUploading(false); }
  };

  const handlePasswordSubmit = async (e) => {
    if (e) e.preventDefault();
    if (!passwordTargetFile) return;
    const updated = { ...filePasswords, [passwordTargetFile.name]: pdfPassword };
    setFilePasswords(updated);
    setShowPasswordModal(false);
    setPdfPassword('');
    if (uploading) await handleUploadSubmit(selectedFiles, updated);
  };

  const handleCancelPasswordModal = () => {
    setShowPasswordModal(false);
    setPasswordTargetFile(null);
    setPdfPassword('');
    if (passwordTargetFile) {
      const remaining = selectedFiles.filter(f => f.name !== passwordTargetFile.name);
      setSelectedFiles(remaining);
      if (uploading && remaining.length > 0) handleUploadSubmit(remaining, filePasswords);
    }
  };

  // --- Derived table data ---
  const mappedTableData = draftsHook.drafts.map(d => ({
    id: d.id,
    documento: d.document_name,
    clasificación: d.doc_type,
    proveedor: d.extracted_data?.tercero?.name || 'Desconocido',
    nit: d.extracted_data?.tercero?.identification || 'N/A',
    fecha_emisión: d.extracted_data?.buy_date ? d.extracted_data.buy_date.substring(0, 10) : 'N/A',
    total: d.extracted_data?.total || 0,
    raw: d
  }));
  const paginatedTableWrapper = { data: mappedTableData, page: draftsHook.page, page_size: draftsHook.limit, total: draftsHook.totalDrafts };

  // --- Render review inner form ---
  const renderReviewForm = () => (
    <div className="modal-review-layout" style={{ padding: 0 }}>
      <div className="d-flex align-items-center gap-2 p-3 pipeline-info-banner rounded-3 mb-3">
        <AlertCircle size={24} className="text-primary" />
        <div className="small text-muted">Por favor, verifique y asocie las entidades reales correspondientes a su POS para asentar la contabilidad y el inventario.</div>
      </div>
      {review.activeDraft.suggested_accounting?.balance_status === 'unbalanced' && (
        <div className="d-flex align-items-center gap-2 p-3 bg-danger-subtle text-danger rounded-3 border border-danger mb-3">
          <AlertCircle size={24} className="text-danger" />
          <div className="small"><strong>⚠️ Asiento Desbalanceado:</strong> Débitos y créditos no coinciden.</div>
        </div>
      )}
      {review.activeDraft.suggested_accounting?.completeness_status === 'incomplete' && (
        <div className="d-flex align-items-start gap-2 p-3 bg-warning-subtle text-warning-emphasis rounded-3 border border-warning mb-3">
          <AlertCircle size={24} className="text-warning mt-1" />
          <div className="small">
            <strong>⚠️ Advertencia de Completitud:</strong>
            <ul className="mb-0 ps-3 mt-1">
              {(review.activeDraft.suggested_accounting?.completeness_errors || []).map((err, i) => (<li key={i}>{err}</li>))}
            </ul>
          </div>
        </div>
      )}
      {/* Tipo de Documento */}
      <div className="review-section">
        <h4>Tipo de Documento</h4>
        <Form.Group className="mb-3">
          <Form.Label>Clasificación</Form.Label>
          <Form.Select value={review.activeDraft.doc_type} onChange={async (e) => {
            const newType = e.target.value;
            const draftId = review.activeDraft.id;
            const updatedDraft = await draftsHook.handleReclassifyFromTable(draftId, newType);
            if (updatedDraft) {
              await review.handleOpenReview(updatedDraft);
            }
          }}>


            <option value="buy">Compra de Inventario</option>
            <option value="spend">Gasto Operativo</option>
            <option value="bank_statement">Extracto Bancario</option>
          </Form.Select>
        </Form.Group>
      </div>
      {review.activeDraft.doc_type !== 'bank_statement' && (
        <>
          <div className="review-section">
            <h4>Información General</h4>
            <Form.Group className="mb-3">
              <Form.Label>Número de Factura / Cuenta de Cobro</Form.Label>
              <Form.Control type="text" placeholder="Ej. FEBO819, CELSIA-123..." value={review.reviewPayload.documento_compra || ''}
                onChange={(e) => review.setReviewPayload(prev => ({ ...prev, documento_compra: e.target.value }))} />
            </Form.Group>
          </div>
          <div className="review-section">
            <h4>Tercero (Proveedor)</h4>
            <div className="d-flex flex-column gap-2">
              <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-1">
                <div className="small detected-ia-text">
                  Tercero detectado: <strong>{review.reviewPayload.tercero?.name} ({review.reviewPayload.tercero?.identification})</strong>
                </div>
                {review.selectedThird && (review.selectedThird.isNew
                  ? <Chip type="warning">¡Se creará automáticamente!</Chip>
                  : <Chip type="success">¡Ya existe en el POS!</Chip>)}
              </div>
              <ClientInfo tercero={review.selectedThird}
                extractedThird={{ name: review.reviewPayload.tercero?.name || '', identification: review.reviewPayload.tercero?.identification || '', email: review.reviewPayload.tercero?.email || '', phone: review.reviewPayload.tercero?.phone || '', address: review.reviewPayload.tercero?.address || '', municipality: review.reviewPayload.tercero?.municipality || '', postalZoneCode: review.reviewPayload.tercero?.postal_code || '', provider: true, client: false, identificationsId: 2 }}
                type="provider" onSelected={(third) => { review.setSelectedThird(third); review.setReviewPayload(prev => ({ ...prev, tercero: { ...prev.tercero, id: third?.id || '' } })); }} />
            </div>
          </div>
        </>
      )}
      {review.activeDraft.doc_type === 'bank_statement' && (
        <BankStatementReconciliation
          reviewPayload={review.reviewPayload}
          setReviewPayload={review.setReviewPayload}
          bankSubaccounts={bankSubaccounts}
          voucherCandidates={review.voucherCandidates}
          handleBankAccountChange={review.handleBankAccountChange}
          generateDefaultEntryForMovement={review.generateDefaultEntryForMovement}
          companyId={companyId}
        />
      )}
      <LiveVoucherPreview liveVoucher={liveVoucher} />
      {review.activeDraft.doc_type === 'buy' && (
        <div className="review-section">
          <ArrayRelater profile={{ uid: profile?.uid || localStorage.getItem('user_uid') || '', cid: companyId ? String(companyId) : (profile?.cid || localStorage.getItem('cid') || null) }}
            array1={(review.reviewPayload.items || []).map((item, idx) => ({ idx: String(idx), description: item.name || '', quantity: item.amount || 0, unit_price: item.buy_value || 0, total: item.total || 0, tax: item.tax || 0, taxRate: item.tax_rate || 0, sku: item.sku || '', relatedItem: item.relatedItem || null, matchStatus: item.matchStatus || 'pending' }))}
            onChange={review.handleArrayRelaterChange} hideFooter={true} />
        </div>
      )}
      {review.activeDraft.doc_type === 'spend' && (
        <div className="review-section">
          {(review.reviewPayload.service_lines || []).some(line => { const lt = Number(line.tax || 0); const st = lookups.spendTypes.find(s => s.id === Number(line.spend_type_id)); return lt > 0 && st && !st.tax_id; }) && (
            <div className="d-flex align-items-start gap-2 p-3 bg-info-subtle text-info-emphasis rounded-3 border border-info mb-3">
              <AlertCircle size={20} className="mt-1 flex-shrink-0" />
              <div className="small"><strong>ℹ️ IVA detectado:</strong> Al confirmar, el sistema auto-configurará el impuesto.</div>
            </div>
          )}
          <ServiceLineInput onServiceLinesChange={review.handleServiceLinesChange}
            serviceLines={(review.reviewPayload.service_lines || []).map((line, idx) => {
              let st = lookups.spendTypes.find(s => s.id === Number(line.spend_type_id));
              if (!st && String(line.spend_type_id).startsWith('new_')) st = { id: line.spend_type_id, name: line.description || `Gasto ${idx + 1}`, isNew: true };
              let fst = { ...st };
              if (line.spend_type_data) {
                if ('tax' in line.spend_type_data) fst.tax = line.spend_type_data.tax;
                if ('retention' in line.spend_type_data) fst.retention = line.spend_type_data.retention;
              }
              return { ...line, id: line.id || idx + 1, spend_type_data: fst, amount: line.amount };
            })}
            initialSpendTypes={lookups.spendTypes} onRefreshSpendTypes={lookups.fetchLookups} />
        </div>
      )}
      {review.activeDraft.doc_type !== 'bank_statement' && (
        <div className="review-section">
          <h4>Detalles de Pago</h4>
          <PaymentMethodSelector selectedMethods={review.selectedMethods} onSelectMethod={review.handlePayMethodChange}
            onAmountChange={review.handlePayMethodChange} onRemoveMethod={review.handlePayMethodChange}
            enableFormPay={review.activeDraft.doc_type === 'buy'} selectedFormPay={review.setSelectedFormPay} defaultAmount={review.reviewPayload.total} />
          <div className="d-flex justify-content-between align-items-center mt-3 p-2 rounded payment-balance-box border">
            <div><strong>Total:</strong> {formatMoney(review.reviewPayload.total || 0)}</div>
            <div><strong>Asignado:</strong> {formatMoney(review.selectedMethods.reduce((s, m) => s + (Number(m.value) || 0), 0))}</div>
            <div className={Math.abs(Math.round((review.reviewPayload.total || 0) - review.selectedMethods.reduce((s, m) => s + (Number(m.value) || 0), 0))) > 2 ? "text-danger fw-bold" : "text-success fw-bold"}>
              {Math.round((review.reviewPayload.total || 0) - review.selectedMethods.reduce((s, m) => s + (Number(m.value) || 0), 0)) > 2
                ? `Faltan: ${formatMoney(Math.round((review.reviewPayload.total || 0) - review.selectedMethods.reduce((s, m) => s + (Number(m.value) || 0), 0)))}`
                : Math.round((review.reviewPayload.total || 0) - review.selectedMethods.reduce((s, m) => s + (Number(m.value) || 0), 0)) < -2
                  ? `Sobrante: ${formatMoney(Math.abs(Math.round((review.reviewPayload.total || 0) - review.selectedMethods.reduce((s, m) => s + (Number(m.value) || 0), 0))))}`
                  : "¡Pago Cuadrado! ✓"}
            </div>
          </div>
        </div>
      )}
    </div>
  );

  // --- Main render ---
  return (
    <div className="pipeline-manager">
      {(uploading || review.loadingReviewStep || draftsHook.reclassifying) && (
        <div className="processing-overlay">
          <div className="processing-loader-card">
            <Loader2 className="processing-spinner" size={48} />
            <h4>{uploading ? 'Procesando Documentos con IA' : draftsHook.reclassifying ? 'Re-clasificando Documento con IA' : 'Cargando Detalles del Borrador'}</h4>
            <p>{uploading ? 'Analizando datos, extrayendo impuestos y asociando proveedores...' : draftsHook.reclassifying ? 'Reprocesando y reestructurando la información contable...' : review.loadingReviewStep}</p>
          </div>
        </div>
      )}

      {isAiQuotaExceeded && (
        <div className="d-flex align-items-center gap-2 p-3 bg-danger-subtle text-danger-emphasis rounded-3 border border-danger mb-3">
          <AlertCircle size={20} className="flex-shrink-0" />
          <div><strong>Límite de Tokens de IA Superado:</strong> Has agotado tu cupo de tokens de IA para este mes.</div>
        </div>
      )}
      <div className="dropzone-wrapper" style={{ marginBottom: '16px', opacity: isAiQuotaExceeded ? 0.55 : 1, pointerEvents: isAiQuotaExceeded ? 'none' : 'auto' }}>
        <DropzoneArea acceptedFiles={[".xml"]} filesLimit={20} disabled={isAiQuotaExceeded || uploading}
          onDrop={async (acceptedFiles) => {
            if (isAiQuotaExceeded) return;
            const validFiles = acceptedFiles.filter(f => f.name.toLowerCase().endsWith('.xml') || f.type === 'text/xml' || f.type === 'application/xml');
            if (validFiles.length < acceptedFiles.length) {
              dispatchCustomEvent('popup', { message: 'Solo se permiten archivos de factura electrónica XML.', type: 'warning' });
            }
            if (validFiles.length > 0) {
              setSelectedFiles(prev => [...prev, ...validFiles]);
            }
          }}
          onDelete={() => setSelectedFiles([])} showPreviews={false} showPreviewsInDropzone={false} showFileNames={false}
          dropzoneText="Arrastra o haz clic para cargar facturas electrónicas XML de Compra o Gasto" showAlerts={['error']} />
      </div>
      {selectedFiles.length > 0 && (
        <div className="file-list-preview mb-3">
          {selectedFiles.map((f, i) => <div key={i} className="file-preview-pill"><Paperclip size={14} /><span>{f.name}</span></div>)}
        </div>
      )}
      {selectedFiles.length > 0 && (
        <div className="d-flex justify-content-end gap-2 mb-3">
          <ButtonSecondary onClick={() => setSelectedFiles([])} title="Limpiar" />
          <ButtonPrimary disabled={uploading || isAiQuotaExceeded} onClick={() => handleUploadSubmit()} title={uploading ? 'Procesando...' : 'Iniciar Procesamiento con IA'} showLoading={uploading} />
        </div>
      )}

      <div className="drafts-list-container mt-2">
        <h4 style={{ fontSize: '1.1rem', fontWeight: 700, marginBottom: '12px' }}>Documentos en Auxiliar Contable ({draftsHook.totalDrafts})</h4>
        <Table id="pipeline-drafts-table" paginatedData={paginatedTableWrapper}
          getFunction={(pn) => { draftsHook.setPage(pn); draftsHook.fetchDrafts(pn); }}
          headersList={["Documento", "Clasificación", "Proveedor", "NIT", "Fecha Emisión", "Total"]}
          excludeList={["id", "raw"]}
          formats={{
            documento: { type: "tooltip" }, total: { type: "money" },
            clasificación: {
              type: "custom", children: (item) => (
                <select value={item.clasificación} disabled={isAiQuotaExceeded} onClick={e => e.stopPropagation()}
                  onChange={e => { e.stopPropagation(); draftsHook.stageReclassify(item.id, e.target.value); }}
                  style={{
                    border: 'none', background: 'transparent', fontWeight: 600, fontSize: '0.85rem', cursor: 'pointer',
                    color: item.clasificación === 'buy' ? '#0d6efd' : item.clasificación === 'bank_statement' ? '#6f42c1' : '#198754',
                    padding: '2px 4px', borderRadius: '4px', outline: 'none'
                  }}>
                  <option value="buy">Compra</option><option value="spend">Gasto</option><option value="bank_statement">Extracto Bancario</option>
                </select>
              )
            }
          }}
          emptyMessage="¡Buen trabajo! No hay documentos pendientes en el Auxiliar Contable."
          isLoading={draftsHook.loadingList} showSkeleton={true}>
          <ButtonAction
            title={<Eye size={16} />}
            onClick={async (d) => {
              const draft = d.raw;
              if (draft && (draft.doc_type === 'buy' || draft.doc_type === 'spend')) {
                try {
                  const client = lookups.getClient();
                  const res = await firstValueFrom(client.get(`pipeline/drafts/${draft.id}`));
                  const fullDraft = res?.data?.data || res?.data?.draft || res?.data || draft;
                  const base64Data = fullDraft.file_base64 || fullDraft.fileBase64 || draft.file_base64;

                  // Parse XML to get rawContent and invoice metadata
                  let parsedInvoice = {};
                  if (base64Data) {
                    try {
                      const decodedXml = decodeURIComponent(escape(atob(base64Data)));
                      parsedInvoice = parseUblInvoice(decodedXml);
                      parsedInvoice.rawContent = decodedXml;
                    } catch (err) {
                      console.warn("Could not decode file_base64 XML", err);
                    }
                  }
                  parsedInvoice.draft_id = fullDraft.id;
                  
                  const extractedData = fullDraft.extracted_data || {};
                  
                  // Garantizar metadatos básicos de cabecera si falló el parse UBL directo
                  parsedInvoice.id = parsedInvoice.id || extractedData.invoice_number || extractedData.id || draft.document_name;
                  parsedInvoice.supplier = parsedInvoice.supplier || extractedData.tercero?.name || extractedData.supplier_name || 'Proveedor';
                  parsedInvoice.supplierNIT = parsedInvoice.supplierNIT || extractedData.tercero?.identification || extractedData.supplier_nit || '';
                  parsedInvoice.issueDate = parsedInvoice.issueDate || extractedData.buy_date || draft.created_at;

                  if (draft.doc_type === 'buy') {
                    // For buy: include matched items + tercero
                    const itemsWithMatch = (parsedInvoice.items || extractedData.items || []).map((item, idx) => {
                      const extItem = (extractedData.items || [])[idx] || item;
                      let matchedProd = extItem.relatedItem || null;
                      if (!matchedProd && extItem.product_id && lookups.products?.length) {
                        matchedProd = lookups.products.find(p => p.id === Number(extItem.product_id));
                      }
                      return {
                        ...item,
                        idx: item.idx || String(idx),
                        description: item.description || extItem.name || extItem.description,
                        relatedItem: matchedProd,
                        matchStatus: matchedProd ? 'auto' : 'pending'
                      };
                    });
                    parsedInvoice.items = itemsWithMatch;

                    // Fallback para totales si no vinieron en UBL parsed
                    parsedInvoice.subtotal = parsedInvoice.subtotal || extractedData.subtotal || extractedData.total || 0;
                    parsedInvoice.tax = parsedInvoice.tax || extractedData.total_tax || extractedData.tax || 0;
                    parsedInvoice.total = parsedInvoice.total || extractedData.total || 0;

                    const payload = {
                      ...parsedInvoice,
                      prefilled: true,
                      tercero: extractedData.tercero || null,
                      matchedItems: (extractedData.items || []).map((item, idx) => ({
                        id: item.product_id || item.id || idx,
                        xmlName: item.name || item.description || 'Producto XML',
                        matchedName: item.name || 'Sin vincular',
                        buy_value: parseFloat(item.unit_price || item.price || 0),
                        auxsku: item.idx || idx,
                        amount: parseFloat(item.quantity || 1),
                        total: parseFloat(item.total || 0),
                        auxtax: parseFloat(item.tax || 0),
                        ...item,
                      })),
                      selectedMethods: extractedData.selectedMethods?.length
                        ? extractedData.selectedMethods
                        : [{ id: 1, method: 'Tarjeta Débito', value: extractedData.total || 0 }],
                      selectedFormPay: extractedData.selectedFormPay || '1',
                    };
                    dispatchCustomEvent("open-buy-from-xml-with-data", payload);
                  } else {
                    // For spend: resolve spend types, build service_lines, tercero, methods
                    let currentSpendTypes = lookups.spendTypes;
                    if (!currentSpendTypes.length) {
                      try {
                        const stRes = await firstValueFrom(client.get('spends/spend_type?page=1&limit=1000'));
                        currentSpendTypes = stRes?.data?.data || stRes?.data || [];
                      } catch (e) { console.warn('SpendTypes fetch failed', e); }
                    }

                    const serviceLines = (extractedData.service_lines || []).map((line, idx) => {
                      let spend_type_id = line.spend_type_id || '';
                      if (!spend_type_id && line.matched_spend_type_id) {
                        const exists = currentSpendTypes.find(st => st.id === line.matched_spend_type_id);
                        if (exists) spend_type_id = exists.id;
                      }
                      if (!spend_type_id) {
                        const desc = (line.description || '').toLowerCase();
                        const matched = currentSpendTypes.find(st => st.name?.toLowerCase() === desc)
                          || currentSpendTypes.find(st => desc.split(' ').some(w => w.length > 3 && st.name?.toLowerCase().includes(w)));
                        spend_type_id = matched ? matched.id : `new_${idx}`;
                      }
                      const lineAmount = (line.base || line.tax)
                        ? (Number(line.base || 0) + Number(line.tax || 0))
                        : (line.amount || 0);
                      let suggestedSpendTypeData;
                      if (String(spend_type_id).startsWith('new_') && line.suggested_new_spend_type) {
                        suggestedSpendTypeData = { ...line.suggested_new_spend_type, id: spend_type_id, isNew: true };
                      }
                      const spendTypeObj = currentSpendTypes.find(st => st.id === spend_type_id) || suggestedSpendTypeData || line.spend_type_data;
                      return {
                        ...line,
                        id: line.id || idx + 1,
                        spend_type_id,
                        spend_type_data: spendTypeObj,
                        matchedType: spendTypeObj?.name || line.description || 'Tipo de Gasto',
                        xmlDescription: line.description || '',
                        amount: lineAmount,
                        base: line.base || lineAmount,
                        tax: line.tax || 0,
                        retention: line.retention || 0,
                      };
                    });

                    const rawItems = (parsedInvoice.items?.length ? parsedInvoice.items : (extractedData.items || extractedData.service_lines || []));
                    const itemsWithMatch = rawItems.map((item, idx) => {
                      const line = serviceLines[idx] || {};
                      const itemDesc = item.description || item.name || line.xmlDescription || line.description || 'Línea de gasto';
                      const itemAmount = parseFloat(item.total || item.subtotal || item.amount || line.amount || 0);
                      return {
                        ...item,
                        idx: item.idx || String(idx),
                        description: itemDesc,
                        quantity: item.quantity || 1,
                        unit_price: item.unit_price || itemAmount,
                        total: itemAmount,
                        relatedItem: line.spend_type_data || null,
                        matchStatus: line.spend_type_data ? 'auto' : 'pending'
                      };
                    });
                    parsedInvoice.items = itemsWithMatch;

                    // Fallback para totales si no vinieron en UBL parsed
                    parsedInvoice.subtotal = parsedInvoice.subtotal || extractedData.subtotal || extractedData.total || 0;
                    parsedInvoice.tax = parsedInvoice.tax || extractedData.total_tax || extractedData.tax || 0;
                    parsedInvoice.total = parsedInvoice.total || extractedData.total || 0;

                    const payload = {
                      ...parsedInvoice,
                      prefilled: true,
                      tercero: extractedData.tercero || null,
                      serviceLines,
                      selectedMethods: extractedData.selectedMethods?.length
                        ? extractedData.selectedMethods
                        : [{ id: 1, method: 'Tarjeta Débito', value: extractedData.total || 0 }],
                    };
                    dispatchCustomEvent("open-spend-from-xml-with-data", payload);
                  }
                  dispatchCustomEvent('loading-btn-review');
                  return;
                } catch (err) {
                  console.error("Error opening draft in wizard:", err);
                }
              }
              review.handleOpenReview(draft);
            }}
            type="action"
            enableLoadingAnimation={true}
            btnKey="review"
          />
          <ButtonAction title={<Trash2 size={16} />} onClick={(d) => draftsHook.handleDiscardDraft(d.id)} type="action" />
        </Table>
      </div>
      {review.activeDraft && (
        <ModalAtom id="review-modal" onClose={review.handleCloseAttempt} style={{ width: '95vw', maxWidth: '95vw', maxHeight: '95vh' }}>
          <div style={{ padding: '10px' }}>
            <h3 style={{ margin: '0 0 20px 0', fontWeight: 800 }}>Revisar y Validar Borrador: {review.activeDraft.document_name}</h3>
            {review.reviewPayload.file_base64 ? (
              <div className="pipeline-review-split-container">
                <div className="pipeline-preview-pane">
                  {review.reviewPayload.file_mime === 'application/pdf'
                    ? <object data={review.pdfUrl} type="application/pdf" width="100%" height="100%" style={{ border: 'none', borderRadius: '12px' }}>
                      <p>Tu navegador no soporta PDFs. <a href={review.pdfUrl} download>Descárgalo aquí</a>.</p>
                    </object>
                    : (review.reviewPayload.file_mime?.includes('xml') || review.activeDraft.document_name?.toLowerCase().endsWith('.xml'))
                      ? <XmlPreview base64Data={review.reviewPayload.file_base64} />
                      : <div className="d-flex align-items-center justify-content-center h-100 bg-light rounded-3 text-muted">Archivo no previsualizable.</div>}
                </div>
                <div className="pipeline-form-pane" style={{ overflowY: 'auto', maxHeight: '74vh', paddingRight: '10px' }}>{renderReviewForm()}</div>
              </div>
            ) : renderReviewForm()}
            <div className="d-flex justify-content-end gap-2 mt-4">
              <ButtonSecondary onClick={review.handleCloseAttempt} title="Cancelar" />
              <ButtonPrimary onClick={review.handleApproveSubmit}
                disabled={review.approving || (review.activeDraft.doc_type !== 'bank_statement' && (
                  !review.reviewPayload.tercero?.id ||
                  Math.abs(Math.round((review.reviewPayload.total || 0) - review.selectedMethods.reduce((s, m) => s + (Number(m.value) || 0), 0))) > 2 ||
                  (review.activeDraft.doc_type === 'spend' && (review.reviewPayload.service_lines || []).length === 0)
                ))} title={review.approving ? 'Registrando...' : 'Confirmar y Aprobar'} showLoading={true} />
            </div>
          </div>
        </ModalAtom>
      )}
      {showPasswordModal && passwordTargetFile && (
        <ModalAtom id="pdf-password-modal" onClose={handleCancelPasswordModal} style={{ width: '400px', maxWidth: '90vw' }}>
          <div style={{ padding: '15px' }}>
            <h4 style={{ margin: '0 0 15px 0', fontWeight: 700 }}>Archivo Protegido</h4>
            <p className="text-muted" style={{ fontSize: '14px', marginBottom: '15px' }}>
              El archivo <strong>{passwordTargetFile.name}</strong> está protegido. Ingrese la contraseña:
            </p>
            <Form onSubmit={handlePasswordSubmit}>
              <Form.Group className="mb-3">
                <Form.Control type="password" placeholder="Contraseña del PDF" value={pdfPassword} onChange={e => setPdfPassword(e.target.value)} autoFocus required />
              </Form.Group>
              <div className="d-flex justify-content-end gap-2">
                <ButtonBasic type="cancel" onClick={handleCancelPasswordModal} title="Omitir" />
                <ButtonBasic type="submit" onClick={handlePasswordSubmit} title="Enviar" />
              </div>
            </Form>
          </div>
        </ModalAtom>
      )}
      {review.showExitWarning && createPortal(
        <div className="confirm-overlay" style={{ zIndex: 4000 }}>
          <div className="confirm-content">
            <div className="confirm-icon-container"><AlertCircle size={48} className="text-warning" /></div>
            <h4 style={{ margin: 0, fontWeight: 700 }}>¿Deseas guardar los cambios?</h4>
            <p className="confirm-message">Has realizado modificaciones. Puedes guardarlas, descartarlas o seguir editando.</p>
            <div className="d-flex flex-column gap-2" style={{ width: '100%' }}>
              <ButtonPrimary onClick={review.handleSaveDraftChanges} disabled={review.savingDraft} className="w-100" showLoading={review.savingDraft}
                title="Guardar Cambios" />
              <ButtonSecondary onClick={review.handleDiscardLocalChanges} className="w-100" title="Descartar Cambios" style={{ borderColor: 'var(--color-danger)', color: 'var(--color-danger)' }} />
              <ButtonSecondary onClick={() => review.setShowExitWarning(false)} className="w-100" title="Cancelar" />
            </div>
          </div>
        </div>, document.body
      )}
      {draftsHook.pendingReclassify && createPortal(
        <div className="confirm-overlay" style={{ zIndex: 4000 }}>
          <div className="confirm-content">
            <div className="confirm-icon-container"><AlertCircle size={48} className="text-warning" /></div>
            <h4 style={{ margin: 0, fontWeight: 700 }}>¿Cambiar clasificación?</h4>
            <p className="confirm-message">El documento será re-clasificado como <strong>{draftsHook.pendingReclassify.label}</strong> y reprocesado por IA.</p>
            <div className="d-flex flex-column gap-2" style={{ width: '100%' }}>
              <ButtonBasic onClick={async () => {
                const updated = await draftsHook.confirmReclassify();
                if (updated && review.activeDraft?.id === updated.id) {
                  await review.handleOpenReview(updated);
                }
              }} className="w-100" title="Sí, re-clasificar" />

              <ButtonBasic type="cancel" onClick={draftsHook.cancelReclassify} className="w-100" title="Cancelar" />
            </div>
          </div>
        </div>, document.body
      )}
      <ConfirmPopUp eventKey="discard-draft" msg="¿Estás seguro de descartar este borrador? Se borrará permanentemente." onAcept={(id) => draftsHook.performDiscardDraft(id)} />
      <BuyFromXml profile={profile} hidden={true} />
      <SpendFromXml profile={profile} hidden={true} />
    </div>
  );
}

export default PipelineDraftsManager;
